import { motion } from "framer-motion";
import { useLanguage } from "../i18n";

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.8, delay, ease: [0.16, 1, 0.3, 1] },
});

export const About = () => {
  const { t } = useLanguage();
  return (
    <section id="about" data-testid="about-section" className="relative py-28 md:py-40 overflow-hidden bg-ink-soft">
      <span className="absolute -top-8 left-4 md:left-10 font-display font-bold text-[26vw] md:text-[18rem] leading-none text-white/[0.03] select-none pointer-events-none" aria-hidden="true">
        02
      </span>
      <div className="relative max-w-7xl mx-auto px-6 md:px-10">
        <motion.p {...fadeUp()} className="font-mono text-xs uppercase tracking-[0.3em] text-brand-cyan mb-6" data-testid="about-chapter-label">
          {t.chapters.about}
        </motion.p>

        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5">
            <motion.div
              {...fadeUp(0.05)}
              className="relative w-44 md:w-56 mb-10"
              data-testid="about-profile-photo"
            >
              <div className="absolute -inset-1 rounded-2xl bg-gradient-to-br from-brand-cyan/60 to-brand-green/60 blur-md opacity-60" aria-hidden="true" />
              <img
                src={`${process.env.PUBLIC_URL}/assets/profile.jpg`}
                alt="Luca Pedano"
                loading="lazy"
                className="relative w-full aspect-[4/5] object-cover object-top rounded-2xl border border-white/10 grayscale-[35%] hover:grayscale-0 transition-[filter] duration-700"
              />
            </motion.div>
            <motion.h2 {...fadeUp(0.05)} className="font-display font-semibold tracking-tight text-4xl md:text-5xl leading-[1.05]" data-testid="about-title">
              {t.about.title}
            </motion.h2>
            <motion.p {...fadeUp(0.1)} className="mt-8 font-mono text-sm md:text-base text-white/60 leading-relaxed" data-testid="about-paragraph-1">
              {t.about.p1}
            </motion.p>
          </div>

          <motion.div {...fadeUp(0.15)} className="lg:col-span-7">
            <h3 className="font-display font-medium text-xl md:text-2xl tracking-tight text-gradient-electric" data-testid="about-dna-title">
              {t.about.dnaTitle}
            </h3>
            <p className="mt-5 font-mono text-sm text-white/55 leading-relaxed" data-testid="about-dna-p1">{t.about.dnaP1}</p>
            <p className="mt-4 font-mono text-sm text-white/55 leading-relaxed" data-testid="about-dna-p2">{t.about.dnaP2}</p>
          </motion.div>
        </div>

        <div className="mt-24">
          <motion.h3 {...fadeUp()} className="font-display font-semibold tracking-tight text-2xl md:text-3xl" data-testid="about-offer-title">
            {t.about.offerTitle}
          </motion.h3>
          <motion.p {...fadeUp(0.05)} className="mt-4 font-mono text-sm text-white/50 max-w-2xl" data-testid="about-offer-intro">
            {t.about.offerIntro}
          </motion.p>

          <div className="mt-10 grid sm:grid-cols-2 gap-4" data-testid="about-pillars">
            {t.about.pillars.map((p, i) => (
              <motion.div
                key={p.title}
                {...fadeUp(i * 0.08)}
                whileHover={{ y: -4 }}
                className={`group rounded-lg border border-white/5 bg-ink-surface p-7 transition-[border-color,box-shadow] duration-500 hover:border-brand-cyan/30 hover:shadow-[0_0_40px_-10px_rgba(0,240,255,0.2)] ${i === t.about.pillars.length - 1 && t.about.pillars.length % 2 === 1 ? "sm:col-span-2" : ""}`}
                data-testid={`about-pillar-${i}`}
              >
                <span className="font-display font-bold text-brand-cyan/60">{String(i + 1).padStart(2, "0")}</span>
                <h4 className="mt-3 font-display font-medium text-lg md:text-xl tracking-tight group-hover:text-brand-cyan transition-colors duration-300">{p.title}</h4>
                <p className="mt-3 font-mono text-xs md:text-[13px] text-white/45 leading-relaxed">{p.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>

        <motion.div {...fadeUp()} className="mt-24 rounded-lg border border-brand-green/20 bg-brand-green/[0.03] p-8 md:p-12" data-testid="about-mission">
          <h3 className="font-display font-semibold tracking-tight text-2xl md:text-3xl text-brand-green" data-testid="about-mission-title">
            {t.about.missionTitle}
          </h3>
          <p className="mt-5 font-mono text-sm md:text-base text-white/60 leading-relaxed max-w-4xl" data-testid="about-mission-text">
            {t.about.missionP}
          </p>
        </motion.div>

        <motion.div {...fadeUp(0.1)} className="mt-14 flex flex-wrap gap-2.5" data-testid="about-chips">
          {t.about.chips.map((c) => (
            <span key={c} className="rounded-full border border-white/10 bg-ink-surface px-4 py-1.5 font-mono text-[11px] text-white/60 hover:border-brand-cyan/40 hover:text-brand-cyan transition-colors duration-300">
              {c}
            </span>
          ))}
        </motion.div>
      </div>
    </section>
  );
};
